import { useEffect, useState } from "react";
import { Phone, Mail, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pagination, usePageSlice } from "@/components/Pagination";
import { toast } from "sonner";

const STATUSES = ["New", "Contacted", "Quoted", "Closed"];
const COLOR = { New: "bg-blue-500/20 text-blue-400", Contacted: "bg-amber-500/20 text-amber-400", Quoted: "bg-purple-500/20 text-purple-400", Closed: "bg-emerald-500/20 text-emerald-400" };
const PER_PAGE = 8;

export default function AdminWholesale() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);

  const load = () => api.get("/admin/wholesale").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => setPage(1), [filter]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/wholesale/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch { toast.error("Could not update"); }
  };

  const filtered = filter === "all" ? items : items.filter((x) => x.status === filter);
  const { paged, totalPages } = usePageSlice(filtered, page, PER_PAGE);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Wholesale Enquiries</h1>
          <p className="text-sm text-muted-foreground">{items.length} total · {items.filter((x) => x.status === "New").length} new</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44 bg-card border-border" data-testid="wholesale-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="admin bg-card border-border">
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? <p className="text-muted-foreground">No enquiries yet.</p> : (
        <>
          <div className="space-y-3">
            {paged.map((w) => (
              <div key={w.id} className="bg-card border border-border rounded-2xl p-4" data-testid={`wholesale-${w.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold">{w.business_name}</p>
                      <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${COLOR[w.status] || "bg-secondary"}`}>{w.status}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{w.contact_name}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {w.phone}</span>
                      {w.email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {w.email}</span>}
                      {w.city && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {w.city}</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">{new Date(w.created_at).toLocaleString("en-IN")}</p>
                    <Select value={w.status} onValueChange={(v) => setStatus(w.id, v)}>
                      <SelectTrigger className="w-40 bg-secondary border-border h-9 mt-2" data-testid={`wholesale-status-${w.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="admin bg-card border-border">
                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-border grid sm:grid-cols-2 gap-2 text-sm">
                  {w.product_interest && <p><span className="text-muted-foreground">Interested in:</span> {w.product_interest}</p>}
                  {w.quantity && <p><span className="text-muted-foreground">Quantity:</span> {w.quantity}</p>}
                  {w.message && <p className="sm:col-span-2"><span className="text-muted-foreground">Message:</span> {w.message}</p>}
                </div>
              </div>
            ))}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}
    </div>
  );
}
